import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from '@acitra/i18n';
import { AUTH_CONSTRAINTS } from '@acitra/shared';
import { signIn as apiSignIn } from '@acitra/api-client';
import { getInvitation, signupWithInvitation, type GetInvitationResponse } from '../services/api';
import { useAuthStore } from '../stores/auth-store';

const signupSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    password: z
      .string()
      .min(
        AUTH_CONSTRAINTS.MIN_PASSWORD_LENGTH,
        `Password must be at least ${AUTH_CONSTRAINTS.MIN_PASSWORD_LENGTH} characters`
      )
      .regex(/[a-z]/, 'Password must contain a lowercase letter')
      .regex(/[A-Z]/, 'Password must contain an uppercase letter')
      .regex(/[0-9]/, 'Password must contain a number')
      .regex(/[^a-zA-Z0-9]/, 'Password must contain a special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SignupFormData = z.infer<typeof signupSchema>;

type Step = 'loading' | 'signup' | 'completing';

export function Signup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchUserTenants, selectTenant, initialize } = useAuthStore();

  const invitationToken = searchParams.get('token');

  const [step, setStep] = useState<Step>('loading');
  const [invitation, setInvitation] = useState<GetInvitationResponse | null>(null);
  const [invitationEmail, setInvitationEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: { firstName: '', lastName: '', password: '', confirmPassword: '' },
  });

  // Fetch invitation on mount (only once)
  useEffect(() => {
    // Skip if we already have invitation data or are in completing step
    if (invitation || step === 'completing') {
      return;
    }

    async function fetchInvitationDetails() {
      if (!invitationToken) {
        setError('Invalid signup link. Please use the link from your invitation email.');
        setStep('signup');
        return;
      }

      try {
        const data = await getInvitation(invitationToken);
        setInvitation(data);
        setInvitationEmail(data.email);
        setStep('signup');
      } catch (err) {
        if (err instanceof Error) {
          if (err.message.includes('404')) {
            setError('This invitation was not found or has expired.');
          } else if (err.message.includes('410')) {
            setError('This invitation has expired or already been used.');
          } else {
            setError(err.message);
          }
        } else {
          setError('Failed to load invitation');
        }
        setStep('signup');
      }
    }

    fetchInvitationDetails();
  }, [invitationToken, invitation, step]);

  const handleSignup = async (data: SignupFormData) => {
    if (!invitation || !invitationToken) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Call the backend to create the user and add to tenant
      const result = await signupWithInvitation({
        token: invitationToken,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
      });

      setStep('completing');

      // Sign in the user
      const signInResult = await apiSignIn(result.email, data.password);

      if (!signInResult.isSignedIn) {
        throw new Error('Failed to sign in after signup');
      }

      // Re-initialize auth store to pick up the new session
      await initialize();

      // Refresh tenants and select the new one
      await fetchUserTenants();
      selectTenant(result.tenantId);

      // Navigate to the app
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof Error) {
        // Check for specific errors
        if (err.message.includes('already exists')) {
          setError('An account with this email already exists. Please sign in instead.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to create account');
      }
      setStep('signup');
      setIsSubmitting(false);
    }
  };

  const handleGoToLogin = () => {
    if (invitationToken) {
      sessionStorage.setItem('invitation_return_token', invitationToken);
    }
    navigate('/login');
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Error state without invitation
  if (!invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Completing signup state
  if (step === 'completing') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Setting up your account...</h2>
          <p className="text-sm text-gray-600">Please wait while we complete your registration.</p>
        </div>
      </div>
    );
  }

  // Signup form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center mb-6">
          <h1 className="text-xl font-semibold text-gray-900">{t('common.appName')}</h1>
          <p className="text-sm text-gray-600 mt-1">Create your account to join</p>
          <p className="text-base font-medium text-gray-900 mt-2">{invitation.tenantName}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
          {/* Email - shown as info (tied to invitation) */}
          {invitationEmail && (
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                type="email"
                value={invitationEmail}
                disabled
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500">Email is tied to your invitation</p>
            </div>
          )}

          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              First Name
            </label>
            <input
              {...signupForm.register('firstName')}
              type="text"
              autoComplete="given-name"
              placeholder="John"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {signupForm.formState.errors.firstName && (
              <p className="mt-1 text-sm text-red-600">
                {signupForm.formState.errors.firstName.message}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              Last Name
            </label>
            <input
              {...signupForm.register('lastName')}
              type="text"
              autoComplete="family-name"
              placeholder="Doe"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {signupForm.formState.errors.lastName && (
              <p className="mt-1 text-sm text-red-600">
                {signupForm.formState.errors.lastName.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              {...signupForm.register('password')}
              type="password"
              autoComplete="new-password"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {signupForm.formState.errors.password && (
              <p className="mt-1 text-sm text-red-600">
                {signupForm.formState.errors.password.message}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              At least {AUTH_CONSTRAINTS.MIN_PASSWORD_LENGTH} characters with uppercase, lowercase,
              number, and special character
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <input
              {...signupForm.register('confirmPassword')}
              type="password"
              autoComplete="new-password"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {signupForm.formState.errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">
                {signupForm.formState.errors.confirmPassword.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              onClick={handleGoToLogin}
              className="text-primary-600 hover:text-primary-500 font-medium"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
